import { useEffect, useMemo, useState } from 'react';

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';

export type DeveloperPageDialogsProps = {
  reloadDialogOpen: boolean;
  setReloadDialogOpen: (open: boolean) => void;
  handleReloadAllCommits: () => void;

  downloadReasonDialogOpen: boolean;
  setDownloadReasonDialogOpen: (open: boolean) => void;

  downloadReason: string;
  setDownloadReason: (reason: string) => void;
  confirmAdminDownloadRepoZip: () => void;
};

export function DeveloperPageDialogs({
  reloadDialogOpen,
  setReloadDialogOpen,
  handleReloadAllCommits,
  downloadReasonDialogOpen,
  setDownloadReasonDialogOpen,
  downloadReason,
  setDownloadReason,
  confirmAdminDownloadRepoZip,
}: DeveloperPageDialogsProps) {
  const [reloadConfirmText, setReloadConfirmText] = useState('');

  useEffect(() => {
    if (!reloadDialogOpen) setReloadConfirmText('');
  }, [reloadDialogOpen]);

  const canConfirmReload = useMemo(() => reloadConfirmText.trim().toLowerCase() === 'reload', [reloadConfirmText]);

  return (
    <>
      <AlertDialog open={reloadDialogOpen} onOpenChange={setReloadDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reload all commits?</AlertDialogTitle>
            <AlertDialogDescription>
              This clears the commit list stored in Firestore and re-downloads every commit from GitHub. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Input
              placeholder="Type reload to confirm"
              value={reloadConfirmText}
              onChange={(event) => setReloadConfirmText(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">Type <span className="font-mono">reload</span> to enable the button.</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReloadAllCommits} disabled={!canConfirmReload}>
              Reload commits
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={downloadReasonDialogOpen} onOpenChange={setDownloadReasonDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reason for download</AlertDialogTitle>
            <AlertDialogDescription>
              This reason and a timestamp will be logged when the ZIP download begins.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Input
              placeholder="Why are you downloading this snapshot?"
              value={downloadReason}
              onChange={(event) => setDownloadReason(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Logged fields include version, commit SHA (when available), and file count.
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAdminDownloadRepoZip}>Download ZIP</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
